import { useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import { Select } from 'antd'
import axios from 'axios'
import { useSelectedWeek } from '../../../../../../contexts/SelectedWeekContext'
import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange, seasonReferenceYear } from '../../../../../../../_metronic/helpers'
interface DataItem {
  week: string
  fractionPosSarsCov2: number | null
  testSarsCov2Number: number | null
}
const { Option } = Select
interface Trace {
  x: string[]
  y: (number | null)[]
  type: string
  mode: string | undefined
  marker?: { color: string }
  line?: { color: string }
  yaxis?: string
  name: string,
  hovertemplate: any | null
}
interface StreamgraphChartProps {
  season: SeasonRange
  selectedAgeClass: string[]
  setSelectedAgeClass: (v: any) => void
}

const StreamgraphChart: React.FC<StreamgraphChartProps> = ({ season, selectedAgeClass, setSelectedAgeClass }) => {
  const [data, setData] = useState<DataItem[]>([])
  const { selectedWeek, setSelectedWeek } = useSelectedWeek('2025-W40')
  const [ageClass, setAgeClass] = useState([])
  // const [selectedAgeClass, setSelectedAgeClass] = useState<string[]>(['0-200'])
  // Sample data
  const [seasonSelectedWeek, setSeasonSelectedWeek] = useState<string | null>(null)
  const startYear = getYearFromWeek(season.startWeek)
  const endYear = getYearFromWeek(season.endWeek)
  const startWeekNumber = getWeekFromWeek(season.startWeek) + 1

  const [plotData, setPlotData] = useState<Trace[]>([])
  const [showArrow, setShowArrow] = useState<boolean>(false)
  const API_URL = process.env.REACT_APP_API_URL
  function parseAgeClasses(ageClasses: string[]): string {
    const parsedString = ageClasses.map((age) => `ageClass%5B%5D=${age}`).join('&')
    return parsedString
  }

  function addMissingWeek(data: DataItem[]): DataItem[] {
    const startWeek = season.startWeek // First week in the data
    const endWeek = season.endWeek // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const newData: DataItem[] = []

    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          fractionPosSarsCov2: null,
          testSarsCov2Number: null,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= `${startYear}-W52`) {
        insertionIndex = i + 1
      } else {
        break
      }
    }

    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  const fetchData = async (age = selectedAgeClass) => {
    try {
      const colors = ['#203879', '#FF5733', '#33FF57', '#5733FF', '#FF33DD', '#33DDFF'] // Define an array of colors
      const colorsHisto = ['#A7ADC0', '#994836', '#556E5A', '#867FA5', '#B188AA', '#64838A']
      const traces: Trace[] = []
      await Promise.all(
        age.map(async (ageClass, index) => {
          // Pass index to get color from colors array
          let filterAge = parseAgeClasses([ageClass])

          let readableAge
          let selectedColor
          switch (ageClass) {
            case '0-200':
              selectedColor = '#203879'
              readableAge = 'All Ages'
              break
            case '< 1 year':
              selectedColor = '#33FF57'
              break
            case '1 to 5':
              selectedColor = '#5733FF'
              break
            case '6 to 11':
              selectedColor = '#FF33DD'
              break
            case '11 to 18':
              selectedColor = '#33DDFF'
              break
            case '18 to 40':
              selectedColor = '#1E778D'
              break
            case '40 to 65':
              selectedColor = '#4B322C'
              break
            default:
              selectedColor = '#FF2F00'
              break
          }
          const response = await axios.get(
            API_URL + `/covid19PositivityRateSamplesTested/listSarsCov?${filterAge}`
          )

          const trace: Trace = {
            x: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                return (
                  (currentYear === startYear && currentWeek >= startWeekNumber) ||
                  (currentYear === endYear && currentWeek <= 27) ||
                  (currentYear > startYear && currentYear < endYear)
                );
              })
              .map((entry) => entry.week),
            y: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                return (
                  (currentYear === startYear && currentWeek >= startWeekNumber) ||
                  (currentYear === endYear && currentWeek <= 27) ||
                  (currentYear > startYear && currentYear < endYear)
                );
              })
              .map((entry) => entry.fractionPosSarsCov2),
            yaxis: 'y2',
            type: 'scatter',
            mode: 'lines',
            line: { color: selectedColor }, // Assign color based on index
            name: `Covid-19 positivity rate -  ${readableAge ?? ageClass}`, // Add age class to trace name
            hovertemplate:
              '<b>%{fullData.name}</b><br>' +
              'Week: %{x}<br>' +
              'Covid-19 positivity rate: %{y}%<extra></extra>',
          }

          traces.push(trace)


        })
      )
      const response = await axios.get(
        API_URL + `/covid19PositivityRateSamplesTested/listSarsCov`
      )
      let endWeek = season?.endWeek?.split('-W')
      const alignedWeek =
        startYear == seasonReferenceYear
          ? selectedWeek
          : computeSelectedWeekForSeason(selectedWeek, season.startWeek)
      const selectWeek = alignedWeek.split('-W')
      const showArrow =
        (Number(selectWeek[0]) > startYear ||
          (Number(selectWeek[0]) === startYear && Number(selectWeek[1]) >= startWeekNumber)) &&
        (Number(selectWeek[0]) < Number(endWeek[0]) ||
          (Number(selectWeek[0]) === Number(endWeek[0]) &&
            Number(selectWeek[1]) <= Number(endWeek[1])))
      setShowArrow(showArrow)
      setSeasonSelectedWeek(alignedWeek);

      const trace3: Trace = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => entry.testSarsCov2Number),
        type: 'bar',
        mode: '',
        marker: { color: '#A7ADC0' },
        name: 'Number of samples tested - All ages',
        hovertemplate:
          '<b>%{fullData.name}</b><br>' +
          'Week: %{x}<br>' +
          'Number of samples tested: %{y}<extra></extra>',
      }
      traces.push(trace3)

      const startWeek = season.startWeek // First week in the data
      const endWeek0 = season.endWeek // Last week in the data
      const existingWeeks = new Set(response.data.map((item) => item.week)) // Set to store existing weeks
      const missingData: DataItem[] = []
      let currentWeek = startWeek
      while (currentWeek !== endWeek0) {
        // Move to the next week
        const [year, weekNum] = currentWeek.split('-W')
        const weekNumber = parseInt(weekNum)
        if (weekNumber < 52) {
          currentWeek = `${year}-W${(weekNumber + 1).toString()}`
        } else {
          currentWeek = `${parseInt(year) + 1}-W1`
        }
        if (!existingWeeks.has(currentWeek)) {
          missingData.push({
            week: currentWeek,
            fractionPosSarsCov2: null,
            testSarsCov2Number: null,
          })
        }
      }
      const missdata = {
        x: missingData.map((entry) => entry.week),
        y: missingData.map((entry) => 0),
        type: 'scatter',
        mode: 'markers',
        yaxis: 'y2',
        name: 'Missing Data',
        marker: { color: '#000000' },
        hovertemplate:
          '<b>%{fullData.name}</b><br>' +
          'Week: %{x}<br>' +
          'Missing Data: %{y}<extra></extra>',
      }
      traces.push(missdata)
      setPlotData(traces)
    } catch (error) {
      console.error('Error fetching data:', error)
      // Handle error or set a default value for data
      setData([])
    }
  }
  useEffect(() => {
    const listAgeClass = async () => {
      try {
        const response = await axios.get(
          API_URL + '/covid19PositivityRateSamplesTested/listAgeClass'
        )
        const data = response.data
        setAgeClass(data)
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }

    listAgeClass()
    fetchData(selectedAgeClass)
  }, [selectedAgeClass, selectedWeek])

  const layout = {
    title: 'Covid-19 positivity rate among samples tested',
    barmode: 'stack',
    bargap: 0.2, // Adjust the gap between bars
    bargroupgap: 0.1, // Adjust the gap between bar groups
    autosize: true,
    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,

          color: '#BBBBBC',
        },
        showarrow: false,
      },
      {
        xref: 'paper',
        yref: 'paper',
        x: 1.03,
        xanchor: 'left',
        y: 0.5,
        yanchor: 'bottom',
        text: '%',
        font: {
          size: 25,

          //color: '#1F77B4',
        },
        showarrow: false,
      },
      {
        x: startYear == seasonReferenceYear ? selectedWeek : seasonSelectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -170,
      },
    ],

    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {
      // autorange: true,
      range: [0, null]
    },
    yaxis2: {
      showgrid: false,
      titlefont: { color: '#203879' },
      tickfont: { color: '#203879' },
      overlaying: 'y',
      side: 'right',
      zeroline: false,
      range: [0, null]
    },
    legend: {
      x: 0,
      y: -0.5, // Adjust the value to position the legend as per your requirement
      orientation: 'h', // 'h' for horizontal, 'v' for vertical
    },
  }

  function handleDataChangeAge(value): void {
    if (value.length === 0) {
      setSelectedAgeClass(['0-200']);
    } else {
      setSelectedAgeClass(value);
    }
  }

  return (
    <>
      <div className='row'>
        <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
          Age group :
        </label>
        <div className='col-6'>
          <Select
            variant='borderless'
            className='border border-gray-500 border-3 rounded'
            style={{ width: '71%' }}
            size='large'
            value={selectedAgeClass}
            onChange={(value) => handleDataChangeAge(value)}
            mode='multiple'
          >

            {ageClass.map((category) => {
              let readableAge
              switch (category) {
                case '< 1 year':
                  readableAge = 'Less than 1 year'
                  break
                case '1 to 5':
                  readableAge = '1 to 5 years'
                  break
                case '6 to 11':
                  readableAge = '6 to 11 years'
                  break
                case '11 to 18':
                  readableAge = '11 to 18 years'
                  break
                case '18 to 40':
                  readableAge = '18 to 40 years'
                  break
                case '40 to 65':
                  readableAge = '40 to 65 years'
                  break
                case '> 65 years':
                  readableAge = '65 years and older'
                  break
                default:
                  readableAge = 'All ages'
                  break
              }
              return (
                <Option key={category} value={category}>
                  {readableAge}
                </Option>
              )
            })}
          </Select>
        </div>
      </div>
      <div className='plotContainer'>
        <Plot
          style={{ width: '100%', height: '450px' }}
          data={plotData}
          layout={layout}
          config={{
            displaylogo: false,
            responsive: true,
          }}
        />
      </div>
    </>
  )
}

export default StreamgraphChart
